import React, { useState, useContext } from 'react';
import Modal from '../../../common/Modal';
import Icon from '../../../common/Icon';
import { Link, useNavigate } from 'react-router-dom';
import {
  createGroup,
  destroyGroup,
  fetchGroup,
  linkSubmission,
  unlinkSubmission,
} from '../../constants/handleLinkedSubmissions';
import { linkedSubmissions as formatLinked } from '../../constants/submissionShow';
import { SubmissionContext } from '../../../../hooks/Contexts';

const LinkedSubmissionsModal = ({
  showLinkedModal,
  toggleLinkedModal,
  setIsLoading,
  setGroupState,
  isParentSubmission,
  canBecomeParent,
}) => {
  const { submission, groupState } = useContext(SubmissionContext);
  const formattedGroupData = formatLinked(submission.id, groupState);
  const linkedSubmissions = formattedGroupData?.links || [];
  const [submissionToAdd, setSubmissionToAdd] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const handleNavigate = (id) => {
    setIsLoading(true);
    navigate(`/manage/submissions/${id}`);
  };

  const handleInputChange = (e) => {
    setSubmissionToAdd(e.target.value);
    if (error) setError('');
  };

  const handleCreateGroup = async () => {
    const validInput = /^[1-9][0-9]*$/;
    if (!validInput.test(submissionToAdd)) {
      return setError('Invalid ID');
    } else if (canBecomeParent) {
      let group = await createGroup(submission.form_id, submission.id);
      if (group.ok) {
        let link = await linkSubmission(group.data.id, submissionToAdd);
        if (link.ok) {
          setGroupState(link.data.group);
          setSubmissionToAdd('');
        } else {
          // fetch new group to reload with at least it's own submission_group
          let reload = await fetchGroup(group.data.id);
          if (reload.ok) setGroupState(reload.data.group);
        }
      }
    }
  };

  const handleCreateLink = async () => {
    const validInput = /^[1-9][0-9]*$/;
    if (!validInput.test(submissionToAdd)) {
      return setError('Invalid ID');
    } else if (groupState?.id) {
      let link = await linkSubmission(groupState.id, submissionToAdd);
      if (link.ok) {
        setGroupState(link.data.group);
        setSubmissionToAdd('');
      }
    }
  };

  const handleRemoveLink = async (submissionGroupID) => {
    await unlinkSubmission(submissionGroupID, (result) => {
      if (result.ok) {
        let filteredLinks = groupState.submission_groups.filter(
          (sg) => sg.id != submissionGroupID
        );
        setGroupState({ ...groupState, submission_groups: filteredLinks });
      }
    });
  };

  const handleDestroyGroup = async () => {
    if (groupState.id) {
      await destroyGroup(groupState.id, (result) => {
        if (result.ok) setGroupState(null);
      });
    }
  };

  const handleCloseModal = () => {
    setSubmissionToAdd('');
    setError('');
    toggleLinkedModal();
  };

  return (
    <Modal
      title='linked-submissions-modal'
      show={showLinkedModal}
      onClose={() => handleCloseModal()}
    >
      <header>
        <h3>Linked Submissions</h3>
      </header>

      <div className='content'>
        {linkedSubmissions.length ? (
          <>
            <h3>Manage linked submissions from the primary submission.</h3>
            <div className='linked-subs-table-wrapper'>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Form</th>
                    <th>Container</th>
                    <th className='control-icons'>&nbsp;</th>
                  </tr>
                </thead>

                <tbody>
                  {linkedSubmissions.map((linked) => (
                    <tr
                      key={linked.submission_id}
                      id={`row-${linked.submission_id}`}
                      className={
                        linked.disabled
                          ? 'current ignore-hover'
                          : 'ignore-hover'
                      }
                    >
                      <td className='no-pointer'>
                        {linked.submission_id}
                        {linked.isParent && (
                          <>
                            &nbsp;
                            <i
                              className='fa-solid fa-star'
                              title='Parent Submission'
                            />
                          </>
                        )}
                      </td>
                      <td className='no-pointer'>{linked.form.name}</td>
                      <td className='no-pointer'>{linked.container_name}</td>
                      <td className='controls inline no-pointer'>
                        {!linked.disabled ? (
                          <>
                            <a
                              onClick={() =>
                                handleNavigate(linked.submission_id)
                              }
                            >
                              <i
                                className='fa-solid fa-arrow-circle-right'
                                aria-hidden='true'
                                title='Go To Submission'
                              ></i>
                            </a>
                            <Link
                              to={`/manage/submissions/${linked.submission_id}`}
                              target='_blank'
                            >
                              <i
                                className='fa-solid fa-up-right-from-square'
                                aria-hidden='true'
                                title='Open in new tab'
                              ></i>
                            </Link>
                          </>
                        ) : (
                          <i>&nbsp;</i>
                        )}

                        {isParentSubmission && !linked.isParent && (
                          <a
                            onClick={() =>
                              handleRemoveLink(linked.submission_group.id)
                            }
                          >
                            <i
                              className='fa-solid fa-unlink'
                              title='Unlink Submission'
                            ></i>
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          canBecomeParent && (
            <>
              <h3>
                No linked submissions found. Create a new group by linking
                another submission.
              </h3>
              <div className='controls'>
                <div className='right'>
                  {error && (
                    <div className='errors'>
                      <span>{error}</span>
                    </div>
                  )}
                  <input
                    type='text'
                    value={submissionToAdd}
                    placeholder='Enter Submission ID'
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateGroup();
                    }}
                    onChange={(e) => handleInputChange(e)}
                  />
                  <button onClick={() => handleCreateGroup()}>
                    Link Submission
                  </button>
                </div>
              </div>
            </>
          )
        )}
        {isParentSubmission && (
          <>
            <h4>
              Unlink All&nbsp;
              <Icon
                onClick={() => handleDestroyGroup(groupState?.id)}
                icon='trash'
              />
            </h4>
            <div className='controls'>
              <div className='right'>
                {error && (
                  <div className='errors'>
                    <span>{error}</span>
                  </div>
                )}
                <input
                  type='text'
                  value={submissionToAdd}
                  placeholder='Enter Submission ID'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateLink();
                  }}
                  onChange={(e) => handleInputChange(e)}
                />
                <button onClick={() => handleCreateLink()}>
                  Link Submission
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default LinkedSubmissionsModal;
